import { Injectable, Inject, NgZone } from '@angular/core';
import {
  HttpClient,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpContextToken
} from '@angular/common/http';
import { Observable, of, switchMap, defer, from, tap, Subscription, ReplaySubject, Subject, throwError, timer } from 'rxjs';
import { concatMap, mergeMap } from 'rxjs/operators';
import {catchError, map, retryWhen} from 'rxjs/operators';
import {SETTINGS_SERVICE, SettingsService, ZITI_DOMAIN_CONTROLLER} from 'ziti-console-lib';
import { HTTP_CLIENT, LoggerService, FeatureService } from '@netfoundry-ui/shared/services';
import { createZitiBrowserClient, ZitiBrowserClient, ZITI_BROWSER_CLIENT_EVENT_NO_SERVICE, ZITI_BROWSER_CLIENT_EVENT_NO_CONFIG_FOR_HOSTNAME, ZITI_BROWSER_CLIENT_EVENT_SESSION_CREATION_ERROR } from '@openziti/ziti-sdk-browser';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { NetworkServiceV2, ZitiControllerService } from "@netfoundry-ui/shared/apiv2";
import { GrowlerData, GrowlerService } from '@netfoundry-ui/shared/growler';

import { isEmpty, isEqual } from 'lodash-es';

export const ZITI_FALLBACK_TOKEN = new HttpContextToken<boolean>(() => false);

export class AsyncMutex {
  private mutex = Promise.resolve();

  lock<T>(fn: () => Promise<T>): Promise<T> {
    const run = async () => {
      /* eslint-disable-next-line no-useless-catch */
      try {
        return await fn();
      } catch (err) {
        throw err;
      }
    };

    // Chain onto the previous mutex
    const current = this.mutex;
    let release: () => void;
    this.mutex = new Promise<void>(resolve => (release = resolve));

    return current.then(() => run()).finally(() => {if (release) release()});
  }

  async runExclusive<T>(fn: () => Promise<T>): Promise<T> {
    return this.lock(fn);
  }
}

@Injectable({
    providedIn: 'root',
})
export class ZitiApiInterceptor implements HttpInterceptor {

    zitiBrowserClient:ZitiBrowserClient = null;
    private zitiBrowserClientFailureEncountered = false;
    subscription = new Subscription();
    currentEdgeControllerHostname = '';
    private queue$ = new Subject<Observable<HttpEvent<any>>>();
    private mutex = new AsyncMutex();

    constructor(
      @Inject(SETTINGS_SERVICE) private settingsService: SettingsService,
      @Inject(ZITI_DOMAIN_CONTROLLER) public zitiControllerService: ZitiControllerService,
      @Inject(HTTP_CLIENT)private httpClient: HttpClient,
      private logger: LoggerService,
      private ngZone: NgZone,
      private growlService: GrowlerService,
      public networkService: NetworkServiceV2,      
      private featureService: FeatureService
    ) {
      // Serially process requests that hit the mgmt API in a queue
      this.queue$
        .pipe(concatMap(obs$ => obs$))
        .subscribe(); // just kicks off the processing

      // Re-init the browser SDK any time the selected network changes in the UI
      this.subscription.add(
        this.zitiControllerService.zitiSettings.subscribe( async (settings) => {
          if (!isEmpty(settings.zitiDomain)) {
            await this._initZitiBrowserSDK();
          }
        })
      );

    }

    private async doAsyncWork( req: HttpRequest<any> ): Promise<HttpResponse<any>> {

      let resp = new HttpResponse();

      await this._initZitiBrowserSDK(); // no-op if already initialized

      /* eslint-disable-next-line no-useless-catch */
      try {
        resp = await Promise.resolve().then(() =>
          this.zitiBrowserClientFetch(req)
        );
      } catch (err) {
        throw err; // critical: re-throw to allow upstream catch
      }

      return resp;
    }

    /**
     *  Initialize the browser SDK any time the selected network has changed
     */
    async _initZitiBrowserSDK() {

      const selectedEdgeControllerUrl = new URL(this.settingsService.settings.selectedEdgeController);

      this.mutex.runExclusive(async () => {

        if (!isEqual( this.currentEdgeControllerHostname, selectedEdgeControllerUrl.hostname)) {

          this.currentEdgeControllerHostname = selectedEdgeControllerUrl.hostname;

          this.zitiBrowserClient = createZitiBrowserClient({
            logLevel:   'info',             // select your console logLevel (default is 'info')
            logPrefix:  'Ziti-SDK-Browser', // select your arbitrary log prefix

            authorizationParams: {
                controllerHost:   selectedEdgeControllerUrl.hostname,       // tell browser SDK what Ziti Controller to talk to
                controllerPort:   selectedEdgeControllerUrl.port,           //  "    "

                apiSessionToken:  this.settingsService.settings.session.id, // tell browser SDK what the currently logged in user's zt-session token is
            },
          });

          await this.zitiBrowserClient.ready; // do not proceed until the browser SDK has fully initialized

          // Set up handlers for certain zitiBrowserClient events
          this.zitiBrowserClient.on(ZITI_BROWSER_CLIENT_EVENT_NO_SERVICE, this._noServiceEventHandler);
          this.zitiBrowserClient.on(ZITI_BROWSER_CLIENT_EVENT_NO_CONFIG_FOR_HOSTNAME, this._noConfigForHostnameEventHandler);
          this.zitiBrowserClient.on(ZITI_BROWSER_CLIENT_EVENT_SESSION_CREATION_ERROR, this._sessionCreationErrorEventHandler);

          this.zitiBrowserClientFailureEncountered = false; // Reset this when network changes.
        }
      });
    }

    // NOTE: ensure we use an arrow func here, else the 'this' value will be that of the Event emitter, 
    //       and not the ZitiApiInterceptor instance :O
    private _noServiceEventHandler = (noServiceEvent: any) => {
      this.zitiBrowserClientFailureEncountered = true;
      this.growlService.show(new GrowlerData(
        'error', 
        'Error', 
        'An error occurred', `Ziti Service [${noServiceEvent.serviceName}] is not present in the selected network`));
    }
    private _noConfigForHostnameEventHandler = (noConfigForHostnameEvent: any) => {
      this.zitiBrowserClientFailureEncountered = true;
      this.growlService.show(new GrowlerData(
        'error', 
        'Error', 
        'An error occurred', `Hostname [${noConfigForHostnameEvent.hostName}] not present in any intercept-configs in the selected network`));
    }
    private _sessionCreationErrorEventHandler = (sessionCreationErrorEvent: any) => {
      this.zitiBrowserClientFailureEncountered = true;
      this.growlService.show(new GrowlerData(
        'error', 
        'Error', 
        'An error occurred', `${sessionCreationErrorEvent.error}`));
    }

    /**
     *  Do an HTTP fetch, over Ziti, to a protected resource
     */
    async zitiBrowserClientFetch( request: HttpRequest<any>): Promise<HttpResponse<any>> {
      
      // Grab some things needed to form a ziti-fetch-compatible request
      const method  = request.method;
      const url     = request.urlWithParams;
      const body    = request.body ? JSON.stringify(request.body) : undefined;

      // Convert Angular HttpHeaders to plain object
      const headers: Record<string, string> = {};
      request.headers.keys().forEach((key) => {
        const value = request.headers.get(key);
        if (value !== null) headers[key] = value;
      });

      const zitiRequestInit: any = {
        serviceScheme:  `https:`,
        method,
        headers,
        body,
        credentials:    request.withCredentials ? 'include' : 'same-origin'
      };
      
      // Do the async HTTP Request over Ziti
      let zitiResponse;
      /* eslint-disable-next-line no-useless-catch */
      try {
        zitiResponse = await Promise.resolve().then(() =>
          this.zitiBrowserClient.fetch(url, zitiRequestInit)
        );
      } catch (err) {
        throw err; // critical: re-throw to allow upstream catch (i.e. the 'retryWhen' clause in teh intercept)
      }


      let parsedBody: any;
      switch (request.responseType) {
        case 'text':
          parsedBody = await zitiResponse.text();
          break;
        case 'blob':
          parsedBody = await zitiResponse.blob();
          break;
        case 'arraybuffer':
          parsedBody = await zitiResponse.arrayBuffer();
          break;
        case 'json':
        default:
          parsedBody = await zitiResponse.json();
          break;
      }

      // Convert zitiResponse HttpHeaders to plain object
      const responseHeaders: Record<string, string> = {};
      zitiResponse.headers.keys().forEach((key:any) => {
        const value = zitiResponse.headers.get(key);
        if (value !== null) responseHeaders[key] = value;
      });
      
      // Transform the zitiResponse into an Angular-compatible response
      const angularResponse = new HttpResponse({
        body: parsedBody,
        status: zitiResponse.status,
        statusText: zitiResponse.statusText,
        headers: new HttpHeaders(responseHeaders),
        url: request.url,
      });

      return angularResponse;
    }
    
    /**
     *  Decide if an HTTP fetch should go over Ziti, or not
     */
    shouldRouteOverZiti( req: HttpRequest<any> ): boolean /* Promise<Observable<HttpEvent<any>>> */ {

      // Set up filters by target hostname and target path
      const selectedEdgeControllerUrl = new URL(this.settingsService.settings.selectedEdgeController);
      const selectedEdgeControllerHost = selectedEdgeControllerUrl.hostname;
      const urlHost = new URL(req.url).host;
      const isApiPath = req.url.includes('/edge/management/v1');

      // Skip Ziti if not matching host/path
      if (!isEqual(urlHost, selectedEdgeControllerHost) || !isApiPath) {
        return false;
      }
      
      // We matched the controller host/path, so route over Ziti
      return true;
    }


    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

      // Bypass the request if we do not have a Ziti session yet
      if (isEmpty(this.settingsService?.settings?.session) || isEmpty(this.settingsService?.settings?.selectedEdgeController)) {
        return next.handle(req);
      } 

      // Bypass the request if is NOT targeting controller mgmt API
      if (!this.shouldRouteOverZiti( req )) {
        return next.handle(req);
      }

      this.logger.info(`intercept entered for ${req.url}`)

      // Only add the auth policyId & token IFF this is not a fallback request 
      // (i.e. it's being re-driven over REST due to a failed attempt over Ziti)
      if (!req.context.get(ZITI_FALLBACK_TOKEN)) {
        req = this.addAuthPolicyId(req);
        req = this.addAuthToken(req);
      }

      // If the selected network changed, we need to reset the error indicator (which may have tripped on the previously-selected network).
      // This allows us to initially attempt Ziti connections on requests for the newly-selected network.
      const selectedEdgeControllerUrl = new URL(this.settingsService.settings.selectedEdgeController);
      if (!isEqual( this.currentEdgeControllerHostname, selectedEdgeControllerUrl.hostname)) {
        this.zitiBrowserClientFailureEncountered = false;
      }

      // If we previously encountered an error attempting to go over Ziti, then route via a (non-Ziti) REST call
      if (this.zitiBrowserClientFailureEncountered) {
        return next.handle(req);
      }

      // Create a deferred observable for the request that will run over Ziti when the queue gets to it
      const queued$ = defer(() => {
        return from(this.ngZone.run(() => this.doAsyncWork(req))).pipe(
          concatMap((event: HttpEvent<any>) => {
            const httpResponse = event as HttpResponse<any>;
            return of(httpResponse);                
          }),
          retryWhen(errors => {
            return errors.pipe(
              mergeMap((err, retryCount) => {
                const maxRetries = 3;
                this.logger.info(`retryWhen entered for ${req.url} retryCount[${retryCount}]`)
                if (retryCount < maxRetries) {
                  return timer(500);
                }
              })
            );
          }),
          // Final fallback to native HTTP if Ziti fails
          catchError(err => {
            this.zitiBrowserClientFailureEncountered = true;
            this.logger.info(`fallback to native HTTP for ${req.url}`)
            const fallbackReq = req.clone({
              context: req.context.set(ZITI_FALLBACK_TOKEN, true)
            });
            return this.httpClient.request(fallbackReq);
          })
        );
      });

      // Queue the request
      this.queue$.next(queued$);

      // Return the observable to original caller
      return queued$;       
    }

    private addAuthToken(request: HttpRequest<any>) {
        if (request.url.indexOf(this.settingsService?.settings?.selectedEdgeController) < 0) {
          return request;
        }
        const session = this.settingsService.settings.session;
        return request.clone({ setHeaders: { 'zt-session': session.id } });
    }

    private addAuthPolicyId(request: HttpRequest<any>) {
      const url = request.url;
      const authPolicyId = this.settingsService.settings.nfAdminAuthPolicyId;
      if (this.interceptZitiIdentitiesRequest(request, authPolicyId)) {
        let filter: any = request.params.get('filter')?.toString();
        if (isEmpty(filter)) {
          const urlParams = new URLSearchParams(new URL(url).search);
          filter = urlParams.get('filter')?.toString();
          if (isEmpty(filter)) {
            filter = `authPolicyId != "${authPolicyId}"`;
            request = request.clone({
              params: request.params.set('filter', filter)
            });
          } else {
            filter += ` and authPolicyId != "${authPolicyId}"`;
            filter = filter.replace('name contains "%" and ', '');
            filter = filter.replace('name contains "%"', '');
            request = request.clone({
              url: this.setURLParam(url, 'filter', filter)
            });
          }
        } else {
          filter += `authPolicyId != "${authPolicyId}"`;
          request = request.clone({
            params: request.params.set('filter', filter)
          });
        }
      }
      return request;
    }

    private interceptZitiIdentitiesRequest(request, authPolicyId) {
      if (this.featureService.showManagedEntities) {
        return;
      }
      const url = request.url;
      const urlWithoutParams = url.split('?')[0];
      const matchString = 'edge/management/v1/identities';
      if (request.method !== 'GET' || isEmpty(authPolicyId) || urlWithoutParams.indexOf(matchString) < 0) {
        return false;
      }
      const urlLength = urlWithoutParams.length;
      const matchStringLength = matchString.length;
      const maxMatchStringIndexPlusLength = urlWithoutParams.indexOf(matchString) + matchStringLength + 1;
      return maxMatchStringIndexPlusLength >= urlLength;
    }

    setURLParam(url: any, param: any, value: any) {
      const urlObj = new URL(url);
      const searchParams = urlObj.searchParams;
      searchParams.set(param, value);
      return urlObj.toString();
    }

    getZitiData(type: string, paging: any) {
      const apiVersions = this.settingsService.apiVersions || {};
      const prefix = apiVersions["edge-management"]?.v1?.path || '/edge/management/v1';
      const url = this.settingsService.settings.selectedEdgeController;
      const serviceUrl = url + prefix + "/" + type;

      return this.httpClient.get(serviceUrl,{}).pipe(
          catchError((err: any) => {
            const error = "Server Not Accessible";
            this.logger.error('Error getting identities for summary');
            throw(err);
          }),
          map((results: any) => {
            return results;
          })
      );
    }
}
